import { useState, useRef, useCallback } from 'react'
import { Upload, File } from 'lucide-react'

interface FileUploadProps {
  accept: string
  onFileSelect: (file: File) => void
  label: string
}

export default function FileUpload({ accept, onFileSelect, label }: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      setSelectedFile(file)
      onFileSelect(file)
    }
  }, [onFileSelect])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      setSelectedFile(file)
      onFileSelect(file)
    }
  }, [onFileSelect])

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-muted mb-2">
        {label}
      </label>
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative flex flex-col items-center justify-center w-full h-32 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
          isDragOver
            ? 'border-accent bg-accent-soft'
            : selectedFile
              ? 'border-[#5ec27a]/50 bg-[#5ec27a]/[0.06]'
              : 'border-stroke-2 bg-bg hover:border-faint hover:bg-surface'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />

        {selectedFile ? (
          <div className="flex items-center gap-3 text-fg">
            <File size={24} className="text-[#5ec27a]" />
            <div className="flex flex-col">
              <span className="text-sm font-medium truncate max-w-[200px]">
                {selectedFile.name}
              </span>
              <span className="text-xs text-faint">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted">
            <Upload
              size={24}
              className={`transition-colors ${isDragOver ? 'text-accent' : ''}`}
            />
            <div className="text-center">
              <p className="text-sm">
                <span className="font-medium text-fg">点击上传</span> 或拖拽文件到此处
              </p>
              <p className="text-xs text-faint mt-1">
                支持格式: {accept}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
